import { ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { environment } from '@environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { routes } from './app.routes';
import { appInitializer } from '@app/utils/providers/app-initializer.provider';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideTransloco } from '@jsverse/transloco';
import { translocoConfig } from '@app/transloco.config';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { TranslationDomainService } from '@app/translation/domain/translation-domain.service';
import { AuthInterceptor } from '@ess-front/shared/auth';
import { TranslationService } from '@ess-front/shared/translation';
import { WINDOW } from '@ess-front/shared/tokens';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { CustomDateAdapter } from '@app/utils/adapters/custom-date.adapter';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { CustomDatepickerIntl } from '@app/utils/adapters/custom-date-picker-intl.service';
import { SupportedLanguage } from './utils/constants/supported-languages';
import * as Sentry from '@sentry/angular';
import { release } from '@release';
import { ErrorsInterceptor } from '@app/utils/interceptors/errors.interceptor';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

Sentry.init({
  denyUrls: ['localhost'],
  dsn: environment.sentryTravelDns,
  enabled: window.location.hostname !== 'localhost',
  environment: environment.production ? 'production' : 'development',
  ignoreErrors: [' 400 ', ' 401 ', ' 403 '],
  release: release,
  beforeSend: event => (window.location.hostname === 'localhost' ? null : event),
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withRouterConfig({ onSameUrlNavigation: 'reload', paramsInheritanceStrategy: 'always' }),
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
      MatSnackBarModule,
      MatNativeDateModule,
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorsInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: true,
      }),
    },
    { provide: 'env', useValue: environment },
    { provide: MAT_DATE_LOCALE, useValue: SupportedLanguage.En },
    { provide: MatDatepickerIntl, useClass: CustomDatepickerIntl },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: WINDOW, useValue: window },
    provideTransloco(translocoConfig),
    provideTranslocoMessageformat(),
    appInitializer,
    {
      provide: TranslationService,
      useClass: TranslationDomainService,
    },
    provideAnimationsAsync(),
  ],
};
